<template>
  <v-container fluid class="fill-height">
    <v-row align="center" justify="center" no-gutters>
      <v-col cols="12" sm="9" md="6" lg="4">
        <v-card class="elevation-12">
          <div class="text-right pr-2 pt-2">
            <LanguageSelector />
          </div>

          <v-img
            :src="require('@/assets/LTOU_logo.png')"
            height="40"
            width="300"
            class="ma-auto"
            contain
          />

          <v-form ref="form" autocomplete="off" @submit.prevent="login">
            <v-card-text>
              <v-text-field
                v-model="username"
                :label="$t('username')"
                prepend-icon="mdi-account"
                type="email"
                autofocus
                autocomplete="off"
              />

              <PasswordTextField v-model="password" />

              <div v-if="passwordValidationError" class="v-messages error--text">
                {{ passwordValidationError }}
              </div>
            </v-card-text>

            <v-card-actions>
              <v-btn
                :loading="isRequestPending"
                :disabled="isRequestPending"
                color="primary"
                type="submit"
                block
              >
                {{ $t('login') }}
              </v-btn>
            </v-card-actions>
          </v-form>

          <div class="text-center pb-4">
            <router-link :to="{ name: 'forgotPassword' }">
              {{ $t('forgot_password') }}
            </router-link>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <TwoFactorLoginDialog
      v-model="twoFactorDialog"
      :email="username"
      @success="login"
      @cancel="on2FACancel"
    />
  </v-container>
</template>


<script>
import LanguageSelector from '../components/LanguageSelector';
import authService from '../api/auth-service';
import userService from '../api/user-service';
import eventBus, { OPEN_SNACKBAR, OPEN_TWO_FACTOR_REMINDER } from '../util/event-bus';
import PasswordTextField from '@/components/PasswordTextField';
import TwoFactorLoginDialog from '@/components/TwoFactorLoginDialog.vue';

export default {
  name: 'Login',
  components: {
    PasswordTextField,
    LanguageSelector,
    TwoFactorLoginDialog,
  },
  data() {
    return {
      username: '',
      password: '',
      isPasswordHidden: true,
      isRequestPending: false,
      passwordValidationError: null,

      twoFactorDialog: false,
    };
  },
  beforeDestroy() {
    this.passwordValidationError = null;
  },
  methods: {
    async finishLoginFlow() {
      const loginRes = await authService.login(this.username, this.password);
      await this.$store.dispatch('auth/handleAuthData', loginRes.data);
      await this.$store.dispatch('expiration/checkTokenExpiration');

      const userRes = await userService.getCurrent();
      await Promise.allSettled([
        this.$store.dispatch('auth/getCurrentUser', userRes.data),
        this.$store.dispatch('auth/checkShouldImportUsers'),
      ]);

      if (userRes.data && userRes.data.show_two_factor_reminder) {
        eventBus.$emit(OPEN_TWO_FACTOR_REMINDER);
      }

      eventBus.$emit(OPEN_SNACKBAR, { text: this.$t('login_success') })
      await this.$router.push(this.$store.state.auth.lastAccessedRoute || { path: '/' });
    },

    async login() {
      this.isRequestPending = true;
      this.passwordValidationError = null;

      try {
        await this.finishLoginFlow();
      } catch (e) {
        const status = e?.response?.status;
        const body   = e?.response?.data;

        // 2FA is required
        if (status === 403 && body?.message === 'two_factor.otp_code_required') {
          this.twoFactorDialog = true;
          this.isRequestPending = false;
          return;
        }

        // First time QR generation is required (setup)
        if (status === 428 && body?.message === 'two_factor.setup_required') {
          this.isRequestPending = false;
          this.$router.push({ name: 'profile' });
          eventBus.$emit(OPEN_SNACKBAR, this.$t('two_factor.setup_required'));
          return;
        }

        // Other errors
        if (status === 400) {
          eventBus.$emit(OPEN_SNACKBAR, this.$t('invalid_credentials'));
        } else if (status === 401) {
          eventBus.$emit(OPEN_SNACKBAR, this.$t('http_errors.401'));
        } else if (status === 409) {
          this.passwordValidationError = body?.message;
        }

        this.isRequestPending = false;
      }
    },

    on2FACancel() {
      this.twoFactorDialog = false;
      this.isRequestPending = false;
    },
  },
};
</script>
